import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../services/api";
import { useSocket } from "./useSocket";
import type { FeedItem } from "../types/Post/post";

interface UseNewPostsResult {
  newPostCount: number;
  loadNewPosts: () => Promise<void>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Tracks incoming FEED_NEW_POST WebSocket events and provides
 * a way to fetch the actual posts when the user is ready.
 *
 * On WebSocket reconnect, polls the feed API to discover posts
 * that were missed during the disconnect window.
 */
export function useNewPosts(
  currentNewestTimestamp: string | null,
  onPostsLoaded: (posts: FeedItem[]) => void
): UseNewPostsResult {
  const [postIds, setPostIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { subscribe, connected } = useSocket();

  const onPostsLoadedRef = useRef(onPostsLoaded);
  useEffect(() => {
    onPostsLoadedRef.current = onPostsLoaded;
  }, [onPostsLoaded]);

  const timestampRef = useRef(currentNewestTimestamp);
  useEffect(() => {
    timestampRef.current = currentNewestTimestamp;
  }, [currentNewestTimestamp]);

  // Track previous connected state to detect reconnect transitions
  const prevConnectedRef = useRef(connected);
  useEffect(() => {
    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = connected;

    // On reconnect: poll the API to discover missed posts instead of just resetting
    if (!wasConnected && connected && timestampRef.current) {
      api.social
        .getNewFeedPosts(timestampRef.current)
        .then((response) => {
          if (response.posts.length > 0) {
            setPostIds((prev) => {
              const next = new Set(prev);
              for (const post of response.posts) {
                next.add(post.post._id);
              }
              return next;
            });
          }
        })
        .catch(() => {
          // Reconciliation is best-effort; don't show errors for background polls
        });
    }
  }, [connected]);

  // Subscribe to FEED_NEW_POST events
  useEffect(() => {
    const unsub = subscribe("FEED_NEW_POST", (data: { postId?: string }) => {
      if (!data?.postId) return;

      setPostIds((prev) => {
        if (prev.has(data.postId!)) return prev;
        const next = new Set(prev);
        next.add(data.postId!);
        return next;
      });
    });

    return unsub;
  }, [subscribe]);

  const reset = useCallback(() => {
    setPostIds(new Set());
    setError(null);
  }, []);

  const loadNewPosts = useCallback(async () => {
    const since = timestampRef.current;
    if (!since || loading) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.social.getNewFeedPosts(since);
      onPostsLoadedRef.current(response.posts);

      // Only remove IDs that were actually returned by the API.
      // Posts that arrived between click and response remain in the Set.
      const returnedIds = new Set(response.posts.map((p) => p.post._id));
      setPostIds((prev) => {
        const next = new Set<string>();
        prev.forEach((id) => {
          if (!returnedIds.has(id)) {
            next.add(id);
          }
        });
        return next;
      });
    } catch (err) {
      // On failure: preserve pending Set, user can retry
      const message = err instanceof Error ? err.message : "Failed to load new posts";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  return {
    newPostCount: postIds.size,
    loadNewPosts,
    loading,
    error,
    reset,
  };
}
